import {
  SignJWT,
  base64url,
  decodeJwt,
  decodeProtectedHeader,
  exportPKCS8,
  exportSPKI,
  generateKeyPair,
  importJWK,
  importPKCS8,
  importSPKI,
  jwtVerify,
  type JWK,
  type JWTPayload,
  type JWTHeaderParameters,
  type ProtectedHeaderParameters,
} from 'jose'
import { ed448 } from '@noble/curves/ed448.js'

export type JsonObject = Record<string, unknown>

export interface DecodedToken {
  header: ProtectedHeaderParameters
  payload: JWTPayload
  normalized: string
}

export interface ExampleKeyPair {
  algorithm: string
  privateKey: string
  publicKey: string
}

const textEncoder = new TextEncoder()

export function decodeToken(token: string): DecodedToken {
  const normalized = token.replace(/\s/g, '')
  if (normalized.split('.').length !== 3) {
    throw new Error('A JWT must contain three dot-separated segments')
  }

  return {
    header: decodeProtectedHeader(normalized),
    payload: decodeJwt(normalized),
    normalized,
  }
}

function parseJwk(value: string): JWK | undefined {
  const trimmed = value.trim()
  if (!trimmed.startsWith('{')) return undefined
  return JSON.parse(trimmed) as JWK
}

function hmacKey(value: string, encoded: boolean): Uint8Array {
  return encoded ? base64url.decode(value.trim()) : textEncoder.encode(value)
}

function isEd448Jwk(value: JWK | undefined): value is JWK & {
  crv: 'Ed448'
  x: string
  d?: string
} {
  return value?.kty === 'OKP' && value.crv === 'Ed448' && typeof value.x === 'string'
}

function encodedSigningInput(header: JsonObject, payload: JsonObject): string {
  const encodedHeader = base64url.encode(
    textEncoder.encode(JSON.stringify(header)),
  )
  const encodedPayload = base64url.encode(
    textEncoder.encode(JSON.stringify(payload)),
  )
  return `${encodedHeader}.${encodedPayload}`
}

async function verificationKey(
  algorithm: string,
  value: string,
  encoded: boolean,
): Promise<CryptoKey | Uint8Array> {
  if (algorithm.startsWith('HS')) return hmacKey(value, encoded)

  const jwk = parseJwk(value)
  if (jwk) return importJWK(jwk, algorithm)
  return importSPKI(value.trim(), algorithm)
}

async function signingKey(
  algorithm: string,
  value: string,
  encoded: boolean,
): Promise<CryptoKey | Uint8Array> {
  if (algorithm.startsWith('HS')) return hmacKey(value, encoded)

  const jwk = parseJwk(value)
  if (jwk) return importJWK(jwk, algorithm)
  return importPKCS8(value.trim(), algorithm)
}

export async function verifyToken(
  token: string,
  keyValue: string,
  encoded = false,
): Promise<void> {
  const decoded = decodeToken(token)
  const algorithm = decoded.header.alg

  if (!algorithm) throw new Error('The JWT header is missing an alg value')
  if (algorithm === 'none') {
    if (!decoded.normalized.endsWith('.')) {
      throw new Error('Unsecured JWTs must have an empty signature')
    }
    return
  }
  if (!keyValue.trim()) throw new Error('Enter a verification key')

  const jwk = parseJwk(keyValue)
  if (algorithm === 'EdDSA' && isEd448Jwk(jwk)) {
    const [header, payload, signature] = decoded.normalized.split('.')
    if (!header || !payload || !signature) throw new Error('Invalid Ed448 JWT')
    const valid = ed448.verify(
      base64url.decode(signature),
      textEncoder.encode(`${header}.${payload}`),
      base64url.decode(jwk.x),
    )
    if (!valid) throw new Error('signature verification failed')
    return
  }

  const key = await verificationKey(algorithm, keyValue, encoded)
  await jwtVerify(decoded.normalized, key, { algorithms: [algorithm] })
}

export async function encodeToken(
  header: JsonObject,
  payload: JsonObject,
  keyValue: string,
  encoded = false,
): Promise<string> {
  const algorithm = header.alg
  if (typeof algorithm !== 'string' || !algorithm) {
    throw new Error('The header must include a signing algorithm')
  }

  if (algorithm === 'none') {
    return `${encodedSigningInput(header, payload)}.`
  }

  if (!keyValue.trim()) throw new Error('Enter a signing key')

  const jwk = parseJwk(keyValue)
  if (algorithm === 'EdDSA' && isEd448Jwk(jwk)) {
    if (!jwk.d) throw new Error('The Ed448 private JWK is missing its d value')
    const signingInput = encodedSigningInput(header, payload)
    const signature = ed448.sign(
      textEncoder.encode(signingInput),
      base64url.decode(jwk.d),
    )
    return `${signingInput}.${base64url.encode(signature)}`
  }

  const key = await signingKey(algorithm, keyValue, encoded)
  return new SignJWT(payload)
    .setProtectedHeader(header as JWTHeaderParameters)
    .sign(key)
}

export async function generateExampleKeyPair(
  selection: string,
): Promise<ExampleKeyPair> {
  if (selection === 'Ed448') {
    const { secretKey, publicKey } = ed448.keygen()
    const publicJwk: JWK = {
      kty: 'OKP',
      crv: 'Ed448',
      x: base64url.encode(publicKey),
      alg: 'EdDSA',
      use: 'sig',
    }
    const privateJwk: JWK = {
      ...publicJwk,
      d: base64url.encode(secretKey),
    }
    return {
      algorithm: 'EdDSA',
      privateKey: JSON.stringify(privateJwk, null, 2),
      publicKey: JSON.stringify(publicJwk, null, 2),
    }
  }

  const algorithm = selection === 'Ed25519' ? 'EdDSA' : selection
  const { privateKey, publicKey } = await generateKeyPair(algorithm, {
    extractable: true,
  })

  return {
    algorithm,
    privateKey: await exportPKCS8(privateKey),
    publicKey: await exportSPKI(publicKey),
  }
}

export function tokenPartMarkup(token: string): string {
  const classes = ['header-part', 'payload-part', 'signature-part']
  return token
    .split('.')
    .map((part, index) => {
      const className = classes[index] ?? 'signature-part'
      return `<span class="token-part ${className}">${escapeHtml(part)}</span>`
    })
    .join('<span class="token-dot">.</span>')
}

export function jsonMarkup(value: string): string {
  const tokenPattern =
    /"(?:\\.|[^"\\])*"(?=\s*:)|"(?:\\.|[^"\\])*"|true|false|null|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g
  let output = ''
  let lastIndex = 0

  for (const match of value.matchAll(tokenPattern)) {
    const index = match.index ?? 0
    const token = match[0]
    output += escapeHtml(value.slice(lastIndex, index))

    let className = 'json-number'
    if (token.startsWith('"')) {
      className = /^\s*:/.test(value.slice(index + token.length))
        ? 'json-key'
        : 'json-string'
    } else if (token === 'true' || token === 'false') {
      className = 'json-boolean'
    } else if (token === 'null') {
      className = 'json-null'
    }

    output += `<span class="${className}">${escapeHtml(token)}</span>`
    lastIndex = index + token.length
  }

  return output + escapeHtml(value.slice(lastIndex))
}

export function formatJson(value: unknown): string {
  return JSON.stringify(value, null, 2)
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')
}
